import React, { useState } from 'react';
import { useI18n } from '../i18n/useI18n';

export const ConfigurePanel = ({
  currentUser,
  message,
  setMessage,
  apiFetch,
  isMobileViewport,
  DesktopOnlyNotice,
  // User management
  userManagement,
  loadUsers,
  featureSettings,
  updateTalkerFeature,
  updatePreviewRedactionFeature,
  updateDbManagementFeature,
  userForm,
  setUserForm,
  submitCreateUser,
  passwordResetDrafts,
  setPasswordResetDrafts,
  submitResetPassword,
  userActionLoadingKey,
}) => {
  const { t } = useI18n();
  const [dbActionLoading, setDbActionLoading] = useState('');
  const [dbActionResult, setDbActionResult] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [keyForm, setKeyForm] = useState({ oldKey: '', newKey: '', confirmKey: '' });
  const [keyActionLoading, setKeyActionLoading] = useState(false);
  const [keyActionResult, setKeyActionResult] = useState(null);
  const [keyConfirmDialog, setKeyConfirmDialog] = useState(false);

  if (!currentUser || currentUser.role !== 'admin') {
    return null;
  }

  const handleDbAction = async (action) => {
    setDbActionLoading(action);
    setDbActionResult(null);
    setConfirmDialog(null);
    try {
      const response = await apiFetch(`/configure/database/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || t('configure.db.actionFailed'));
      }
      setDbActionResult({ success: true, message: data.message || t('configure.db.actionSuccess') });
      if (data.message) {
        setMessage(data.message);
      }
    } catch (error) {
      setDbActionResult({ success: false, message: error.message || t('configure.db.actionFailed') });
      setMessage(error.message || t('configure.db.actionFailed'));
    } finally {
      setDbActionLoading('');
    }
  };

  const requestDbAction = (action) => {
    if (action === 'init') {
      setConfirmDialog({
        action,
        title: t('configure.db.initTitle'),
        warning: t('configure.db.initWarning'),
        confirmLabel: t('configure.db.initBtn'),
      });
    } else if (action === 'simulate') {
      setConfirmDialog({
        action,
        title: t('configure.db.simulateTitle'),
        warning: t('configure.db.simulateWarning'),
        confirmLabel: t('configure.db.simulateBtn'),
      });
    }
  };

  const handleDownloadBackup = async () => {
    setDbActionLoading('backup-download');
    setDbActionResult(null);
    try {
      const response = await apiFetch('/configure/database/backup', { method: 'POST' });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || t('configure.db.backupFailed'));
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `backup_${new Date().toISOString().replace(/[:.]/g, '-')}.db`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setDbActionResult({ success: true, message: t('configure.db.backupDownloaded') });
    } catch (error) {
      setDbActionResult({ success: false, message: error.message || t('configure.db.backupFailed') });
      setMessage(error.message || t('configure.db.backupFailed'));
    } finally {
      setDbActionLoading('');
    }
  };

  if (isMobileViewport) {
    return <DesktopOnlyNotice title={t('configure.title')} />;
  }

  const submitChangeKey = async () => {
    setKeyActionResult(null);

    const oldKey = String(keyForm.oldKey || '').trim();
    const newKey = String(keyForm.newKey || '').trim();
    const confirmKey = String(keyForm.confirmKey || '').trim();

    if (!oldKey || !newKey || !confirmKey) {
      setKeyActionResult({ success: false, message: t('configure.security.failed') });
      return;
    }
    if (newKey.length < 6) {
      setKeyActionResult({ success: false, message: t('configure.security.tooShort') });
      return;
    }
    if (newKey !== confirmKey) {
      setKeyActionResult({ success: false, message: t('configure.security.mismatch') });
      return;
    }

    setKeyActionLoading(true);
    setKeyConfirmDialog(false);
    try {
      const response = await apiFetch('/security/change-key', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ old_master_key: oldKey, new_master_key: newKey }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || t('configure.security.failed'));
      }
      setKeyActionResult({ success: true, message: data.message || t('configure.security.success') });
      setKeyForm({ oldKey: '', newKey: '', confirmKey: '' });
      setMessage(data.message || t('configure.security.success'));
    } catch (error) {
      setKeyActionResult({ success: false, message: error.message || t('configure.security.failed') });
      setMessage(error.message || t('configure.security.failed'));
    } finally {
      setKeyActionLoading(false);
    }
  };

  return (
    <section className="panel">
      <div className="panelHeader rowHeader">
        <div>
          <span className="panelKicker">{t('configure.kicker')}</span>
          <h2>{t('configure.title')}</h2>
          <p className="workspaceDesc">{t('configure.desc')}</p>
        </div>
      </div>
      {message ? <p className="messageBanner">{message}</p> : null}

      <div className="monitorDetailGrid">
        {/* ── User Management ── */}
        <section className="monitorPanelCard">
          <div className="panelHeader compactHeader">
            <span className="panelKicker">{t('configure.users.kicker')}</span>
            <h3>{t('configure.users.title')}</h3>
            <button className="ghostButton" onClick={loadUsers}>{t('configure.users.refresh')}</button>
          </div>
          <div className="authFormGrid">
            <label className="authField">
              <span>{t('configure.users.username')}</span>
              <input value={userForm.username} onChange={(event) => setUserForm((current) => ({ ...current, username: event.target.value }))} placeholder={t('configure.users.usernamePlaceholder')} />
            </label>
            <label className="authField">
              <span>{t('configure.users.password')}</span>
              <input type="password" value={userForm.password} onChange={(event) => setUserForm((current) => ({ ...current, password: event.target.value }))} placeholder={t('configure.users.passwordPlaceholder')} />
            </label>
            <label className="authField">
              <span>{t('configure.users.role')}</span>
              <select value={userForm.role} onChange={(event) => setUserForm((current) => ({ ...current, role: event.target.value }))}>
                <option value="user">{t('configure.users.roleUser')}</option>
                <option value="admin">{t('configure.users.roleAdmin')}</option>
              </select>
            </label>
          </div>
          <div className="authActionsRow">
            <button className="primaryButton" onClick={submitCreateUser} disabled={userActionLoadingKey === 'create-user'}>{userActionLoadingKey === 'create-user' ? t('configure.users.creating') : t('configure.users.create')}</button>
          </div>
          <div className="tableWrap darkTableWrap">
            <table>
              <thead>
                <tr>
                  <th>{t('configure.users.colId')}</th>
                  <th>{t('configure.users.colUsername')}</th>
                  <th>{t('configure.users.colRole')}</th>
                  <th>{t('configure.users.colStatus')}</th>
                  <th>{t('configure.users.colCreatedAt')}</th>
                  <th>{t('configure.users.colResetPassword')}</th>
                </tr>
              </thead>
              <tbody>
                {userManagement.users.length ? userManagement.users.map((item) => (
                  <tr key={`user-${item.id}`}>
                    <td>{item.id}</td>
                    <td>{item.username}</td>
                    <td>{item.role}</td>
                    <td>{item.is_active ? t('configure.users.active') : t('configure.users.inactive')}</td>
                    <td>{item.created_at || '-'}</td>
                    <td>
                      <div className="userPasswordResetRow">
                        <input
                          type="password"
                          value={passwordResetDrafts[item.id] || ''}
                          onChange={(event) => setPasswordResetDrafts((current) => ({ ...current, [item.id]: event.target.value }))}
                          placeholder={t('configure.users.resetPlaceholder')}
                        />
                        <button className="secondaryButton" onClick={() => submitResetPassword(item.id)} disabled={userActionLoadingKey === `reset-${item.id}`}>{userActionLoadingKey === `reset-${item.id}` ? t('configure.users.resetting') : t('configure.users.reset')}</button>
                      </div>
                    </td>
                  </tr>
                )) : <tr><td colSpan="6" className="emptyTable">{t('configure.users.noData')}</td></tr>}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── Feature Access ── */}
        <section className="monitorPanelCard featureAccessCard">
          <div className="panelHeader compactHeader">
            <span className="panelKicker">{t('configure.features.kicker')}</span>
            <h3>{t('configure.features.talkerAccess')}</h3>
            <p>{t('configure.features.talkerAccessDesc')}</p>
          </div>
          <label className="featureToggleRow">
            <input
              type="checkbox"
              checked={featureSettings.talker_enabled}
              onChange={(event) => updateTalkerFeature(event.target.checked)}
              disabled={userActionLoadingKey === 'talker-feature'}
            />
            <span className="featureToggleControl" aria-hidden="true" />
            <span><strong>{featureSettings.talker_enabled ? t('configure.features.talkerAvailable') : t('configure.features.talkerUnavailable')}</strong><small>{featureSettings.talker_enabled ? t('configure.features.talkerVisibleHint') : t('configure.features.talkerHiddenHint')}</small></span>
          </label>
          <div className="panelHeader compactHeader">
            <span className="panelKicker">{t('configure.features.privacyKicker')}</span>
            <h3>{t('configure.features.privacyTitle')}</h3>
            <p>{t('configure.features.privacyDesc')}</p>
          </div>
          <label className="featureToggleRow">
            <input
              type="checkbox"
              checked={featureSettings.preview_redaction_enabled}
              onChange={(event) => updatePreviewRedactionFeature(event.target.checked)}
              disabled={userActionLoadingKey === 'preview-redaction-feature'}
            />
            <span className="featureToggleControl" aria-hidden="true" />
            <span><strong>{featureSettings.preview_redaction_enabled ? t('configure.features.previewRedactionAvailable') : t('configure.features.previewRedactionUnavailable')}</strong><small>{featureSettings.preview_redaction_enabled ? t('configure.features.previewRedactionVisibleHint') : t('configure.features.previewRedactionHiddenHint')}</small></span>
          </label>
        </section>

        {/* ── Database Settings ── */}
        <section className="monitorPanelCard databaseSettingsCard">
          <div className="panelHeader compactHeader">
            <span className="panelKicker">{t('configure.db.kicker')}</span>
            <h3>{t('configure.db.title')}</h3>
            <p>{t('configure.db.desc')}</p>
          </div>
          <label className="featureToggleRow">
            <input
              type="checkbox"
              checked={featureSettings.db_management_enabled}
              onChange={(event) => updateDbManagementFeature(event.target.checked)}
              disabled={userActionLoadingKey === 'db-management-feature'}
            />
            <span className="featureToggleControl" aria-hidden="true" />
            <span><strong>{featureSettings.db_management_enabled ? t('configure.features.dbManagementAvailable') : t('configure.features.dbManagementUnavailable')}</strong><small>{featureSettings.db_management_enabled ? t('configure.features.dbManagementVisibleHint') : t('configure.features.dbManagementHiddenHint')}</small></span>
          </label>
          <div className="dbActionGrid">
            <div className="dbActionItem">
              <div className="dbActionInfo">
                <strong>{t('configure.db.initTitle')}</strong>
                <span>{t('configure.db.initDesc')}</span>
              </div>
              <button
                className="secondaryButton"
                onClick={() => requestDbAction('init')}
                disabled={!!dbActionLoading || !featureSettings.db_management_enabled}
              >
                {dbActionLoading === 'init' ? t('configure.db.processing') : t('configure.db.initBtn')}
              </button>
            </div>
            <div className="dbActionItem">
              <div className="dbActionInfo">
                <strong>{t('configure.db.backupTitle')}</strong>
                <span>{t('configure.db.backupDesc')}</span>
              </div>
              <button
                className="secondaryButton"
                onClick={handleDownloadBackup}
                disabled={!!dbActionLoading || !featureSettings.db_management_enabled}
              >
                {dbActionLoading === 'backup-download' ? t('configure.db.processing') : t('configure.db.backupBtn')}
              </button>
            </div>
            <div className="dbActionItem">
              <div className="dbActionInfo">
                <strong>{t('configure.db.simulateTitle')}</strong>
                <span>{t('configure.db.simulateDesc')}</span>
              </div>
              <button
                className="secondaryButton"
                onClick={() => requestDbAction('simulate')}
                disabled={!!dbActionLoading || !featureSettings.db_management_enabled}
              >
                {dbActionLoading === 'simulate' ? t('configure.db.processing') : t('configure.db.simulateBtn')}
              </button>
            </div>
          </div>
          {dbActionResult ? (
            <div className={`dbActionResult ${dbActionResult.success ? 'dbActionResultSuccess' : 'dbActionResultError'}`}>
              {dbActionResult.message}
            </div>
          ) : null}
        </section>

        {/* ── Master Key Management ── */}
        <section className="monitorPanelCard masterKeyCard">
          <div className="panelHeader compactHeader">
            <span className="panelKicker">{t('configure.security.kicker')}</span>
            <h3>{t('configure.security.title')}</h3>
            <p>{t('configure.security.desc')}</p>
          </div>
          <div className="authFormGrid">
            <label className="authField">
              <span>{t('configure.security.oldKey')}</span>
              <input
                type="password"
                value={keyForm.oldKey}
                onChange={(e) => setKeyForm((prev) => ({ ...prev, oldKey: e.target.value }))}
                placeholder={t('configure.security.oldKeyPlaceholder')}
                autoComplete="off"
              />
            </label>
            <label className="authField">
              <span>{t('configure.security.newKey')}</span>
              <input
                type="password"
                value={keyForm.newKey}
                onChange={(e) => setKeyForm((prev) => ({ ...prev, newKey: e.target.value }))}
                placeholder={t('configure.security.newKeyPlaceholder')}
                autoComplete="new-password"
              />
            </label>
            <label className="authField">
              <span>{t('configure.security.confirmKey')}</span>
              <input
                type="password"
                value={keyForm.confirmKey}
                onChange={(e) => setKeyForm((prev) => ({ ...prev, confirmKey: e.target.value }))}
                placeholder={t('configure.security.confirmKeyPlaceholder')}
                autoComplete="new-password"
              />
            </label>
          </div>
          <div className="authActionsRow">
            <button
              className="primaryButton"
              onClick={() => setKeyConfirmDialog(true)}
              disabled={keyActionLoading}
            >
              {keyActionLoading ? t('configure.security.processing') : t('configure.security.changeBtn')}
            </button>
          </div>
          {keyActionResult ? (
            <div className={`dbActionResult ${keyActionResult.success ? 'dbActionResultSuccess' : 'dbActionResultError'}`}>
              {keyActionResult.message}
            </div>
          ) : null}
        </section>
      </div>

      {confirmDialog ? (
        <div className="confirmOverlay" role="dialog" aria-modal="true">
          <div className="confirmDialog">
            <div className="confirmDialogHeader">
              <strong>{confirmDialog.title}</strong>
            </div>
            <div className="confirmDialogBody">
              <p className="confirmDialogWarning">{t('configure.db.backupReminder')}</p>
              <p>{confirmDialog.warning}</p>
            </div>
            <div className="confirmDialogActions">
              <button
                className="ghostButton"
                onClick={() => setConfirmDialog(null)}
                disabled={!!dbActionLoading}
              >
                {t('configure.db.confirmCancel')}
              </button>
              <button
                className="dangerButton"
                onClick={() => handleDbAction(confirmDialog.action)}
                disabled={!!dbActionLoading}
              >
                {dbActionLoading === confirmDialog.action ? t('configure.db.processing') : confirmDialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {keyConfirmDialog ? (
        <div className="confirmOverlay" role="dialog" aria-modal="true">
          <div className="confirmDialog">
            <div className="confirmDialogHeader">
              <strong>{t('configure.security.title')}</strong>
            </div>
            <div className="confirmDialogBody">
              <p className="confirmDialogWarning">{t('configure.security.warning')}</p>
            </div>
            <div className="confirmDialogActions">
              <button
                className="ghostButton"
                onClick={() => setKeyConfirmDialog(false)}
                disabled={keyActionLoading}
              >
                {t('configure.db.confirmCancel')}
              </button>
              <button
                className="dangerButton"
                onClick={submitChangeKey}
                disabled={keyActionLoading}
              >
                {keyActionLoading ? t('configure.security.processing') : t('configure.security.changeBtn')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};
